// src/studio/components/NewProjectModal.jsx
//
// Rebuilt on shared/ui (Modal, FormField, Select) rather than
// rds_ui/components/NewProjectModal.jsx's hand-rolled markup, so the studio
// carries no module CSS of its own (plan D-4). Same reduced field set that
// modal settled on this session: only what the engagement genuinely cannot
// supply. client_name, contract_type and platform come from the engagement
// and are shown read-only, never re-typed — the backend takes them from the
// engagement regardless of anything this form sends (SPEC-07 D-5).
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Plus } from "lucide-react";
import { Modal, FormField, Input, Select, Button, Alert, useDiscardConfirm } from "../../shared/ui";
import { studioConfigApi } from "../api/studioApi";

const PROJECT_TYPES = [
  { value: "ams", label: "AMS / RUN Services", hint: "Managed services, support, SLA-based engagement" },
  { value: "implementation", label: "Platform Implementation", hint: "Salesforce / ServiceNow implementation or rollout" },
  { value: "integration", label: "Integration / Middleware", hint: "Connecting existing systems via APIs or middleware" },
  { value: "custom_dev", label: "Custom Development", hint: "Building new software or applications" },
  { value: "data_analytics", label: "Data & Analytics", hint: "Data pipelines, dashboards, warehouses" },
];

const SERVICE_LINE_GROUPS = [
  { label: "CRM & ERP PLATFORMS", items: ["Salesforce", "NetSuite", "SAP", "Oracle Applications"] },
  { label: "ITSM & WORKFLOW", items: ["ServiceNow"] },
  { label: "CUSTOM DEVELOPMENT", items: [".NET", "Python", "Java"] },
  { label: "FRONTEND FRAMEWORKS", items: ["React", "Angular"] },
  { label: "AI & INTELLIGENCE", items: ["Agentic AI", "AI/ML", "Data & AI"] },
  { label: "CLOUD PLATFORMS", items: ["Azure", "AWS", "GCP"] },
];

const EMPTY = { name: "", methodology: "", project_type: "", service_line: [] };

// Every account name in this system is client_name too (Engagement has no
// name distinct from it — confirmed against EngagementAggregate/DTO, neither
// carries one), so a suggested name has to come from somewhere else that
// actually varies: how many projects already exist for this engagement.
// "Draft N" reads honestly as a placeholder rather than a real name, and
// gives each one a distinct starting point when someone creates several —
// different drafts/versions of the requirements and design documents for
// the same engagement, not just one project per engagement.
function suggestName(accountName, existingProjectsCount) {
  if (!accountName) return "";
  return `${accountName} — Draft ${existingProjectsCount + 1}`;
}

export default function NewProjectModal({
  open, onClose, onSubmit, loading, engagement, accountName, existingProjectsCount = 0,
}) {
  const [form, setForm] = useState(EMPTY);
  const [nameError, setNameError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [methodologies, setMethodologies] = useState([]);
  const [dirty, setDirty] = useState(false);

  // Resets the form the moment `open` flips to true — adjusted during render
  // rather than in an effect (React's own recommended pattern for "reset
  // state when a prop changes"), so opening the modal twice in a row can't
  // flash the previous submission's values before an effect gets to clear
  // them.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setForm({ ...EMPTY, name: suggestName(accountName, existingProjectsCount) });
      setNameError(null);
      setSubmitError(null);
      setDirty(false);
    }
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty, onClose);

  useEffect(() => {
    studioConfigApi.methodologies().then(setMethodologies).catch(() => {});
  }, []);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const toggleServiceLine = (label) => {
    setForm((f) => ({
      ...f,
      service_line: f.service_line.includes(label)
        ? f.service_line.filter((s) => s !== label)
        : [...f.service_line, label],
    }));
    setDirty(true);
  };

  async function handleSubmit() {
    if (!form.name.trim()) {
      setNameError("Draft name is required");
      return;
    }
    setSubmitError(null);
    try {
      await onSubmit({ ...form, service_line: form.service_line.join(", ") });
    } catch (err) {
      setSubmitError(err?.message || "Failed to create project.");
    }
  }

  const inherited = [
    ["Project", engagement?.client_name],
    ["Contract type", engagement?.contract_type],
    ["Platform", engagement?.platform],
  ].filter(([, value]) => value);

  return (
    <>
      <Modal
        open={open}
        title="Create New Draft"
        onClose={requestClose}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={loading}>
              <Plus size={14} /> Create Draft
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {submitError && <Alert variant="danger" title="That didn't work">{submitError}</Alert>}

          <FormField
            label="Draft name"
            required
            error={Boolean(nameError)}
            errorText={nameError}
            helperText="Suggested — edit it to whatever distinguishes this draft."
          >
            <Input
              placeholder="e.g. CRM Modernization Phase 2"
              value={form.name}
              onChange={(e) => { set("name", e.target.value); setNameError(null); }}
            />
          </FormField>

          {inherited.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                Inherited from {engagement?.client_name ? "this engagement" : "the engagement"}
              </p>
              <dl className="grid grid-cols-3 gap-2">
                {inherited.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
                    <dd className="text-xs text-gray-800 truncate" title={value}>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <FormField label="Methodology">
            <Select
              value={form.methodology}
              onChange={(e) => set("methodology", e.target.value)}
              options={[
                { value: "", label: "Select methodology" },
                ...methodologies.map((m) => ({ value: m.code, label: m.name })),
              ]}
            />
          </FormField>

          <FormField label="Project type" helperText="Helps the AI generate accurate design.">
            <div className="flex flex-wrap gap-2">
              {PROJECT_TYPES.map((pt) => {
                const active = form.project_type === pt.value;
                return (
                  <button
                    key={pt.value}
                    type="button"
                    onClick={() => set("project_type", active ? "" : pt.value)}
                    title={pt.hint}
                    className={[
                      "px-3 py-1.5 text-xs rounded-full border transition-all",
                      active
                        ? "bg-brand-50 border-brand-400 text-brand-700 font-medium"
                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300",
                    ].join(" ")}
                  >
                    {pt.label}
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField label="Service lines" helperText="Select the ones that apply.">
            <div className="flex flex-col gap-3">
              {SERVICE_LINE_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="text-xs font-bold tracking-wider text-gray-400 mb-1.5">{group.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((item) => {
                      const active = form.service_line.includes(item);
                      return (
                        <button
                          key={item}
                          type="button"
                          onClick={() => toggleServiceLine(item)}
                          className={[
                            "px-3 py-1.5 text-xs rounded-full border transition-all",
                            active
                              ? "bg-brand-50 border-brand-400 text-brand-700 font-medium"
                              : "bg-white border-gray-200 text-gray-600 hover:border-gray-300",
                          ].join(" ")}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </FormField>
        </div>
      </Modal>
      {confirmDialog}
    </>
  );
}

NewProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  engagement: PropTypes.shape({
    engagement_id: PropTypes.string,
    client_name: PropTypes.string,
    contract_type: PropTypes.string,
    platform: PropTypes.string,
  }),
  accountName: PropTypes.string,
  existingProjectsCount: PropTypes.number,
};
